import type { JobsApi } from '@/features/jobs/api/job-api'
import {
  ConnectJobTaxDataImportRequest,
  GeneratePublicAccessTokenRequest,
  GetJobTaxDataImportRequest,
  InitializeJobTaxDataImportRequest,
  MeasureOneDataRequestItemSelection,
  StartDocumentsUploadRequest,
} from '@taxfyle/api-internal/internal/measure_one_pb'
import {
  mapMeasureOneExperienceToProtos,
  mapMeasureOneSourceDataToProtos,
  mapTaxJobDataImport,
  type MeasureOneExperience,
  type WellKnownDocumentTypeShortId,
} from '@/features/jobs/details/tax-type/info-gathering/measure-one/mappers'
import { filter, map } from 'rxjs'
import { RealtimeEvent } from '@taxfyle/api-internal/internal/realtime_pb'
import { collection } from '@/infra/collection'
import { JobTaxDataImport } from '@/features/jobs/details/tax-type/info-gathering/measure-one/JobTaxDataImport'
import { filterNonNil } from '@/utils/rx-util'
import type { AvailableItem } from '@/features/jobs/details/tax-type/info-gathering/measure-one/MeasureOneWizardView/SelectDocumentsView'
import { Id } from '@/infra/id'
import { groupBy } from 'lodash-es'

export class MeasureOneStore {
  readonly taxJobData = collection({
    create: () => new JobTaxDataImport(),
    getModelId: (m) => m.id,
    getDataId: (d) => d.id,
  })

  constructor(private readonly _api: JobsApi) {
    this._api.realtime$
      .pipe(
        filter(
          (event) =>
            event.getTypeCase() ===
            RealtimeEvent.TypeCase.MEASURE_ONE_JOB_TAX_DATA_IMPORT_UPDATED,
        ),
        map((e) => e.getMeasureOneJobTaxDataImportUpdated()),
        filterNonNil(),
        map((event) => event.getJobTaxDataImport()),
        filterNonNil(),
      )
      .subscribe((proto) => {
        return this.taxJobData.set(mapTaxJobDataImport(proto))
      })
  }

  async initializeJobTaxDataImport(
    jobId: string,
    workspaceId: string,
    experience: MeasureOneExperience,
    documentTypes: WellKnownDocumentTypeShortId[],
  ) {
    const request = new InitializeJobTaxDataImportRequest()
      .setJobId(jobId)
      .setWorkspaceId(workspaceId)
      .setExperienceType(mapMeasureOneExperienceToProtos(experience))
      .setSourceDataTypesList(
        documentTypes.map((documentType) =>
          mapMeasureOneSourceDataToProtos(documentType),
        ),
      )

    const response =
      await this._api.measureOne.initializeJobTaxDataImport(request)

    return response.getDataRequestId()
  }

  async generatePublicAccessToken(jobId: string) {
    const request = new GeneratePublicAccessTokenRequest().setJobId(jobId)

    const response =
      await this._api.measureOne.generatePublicAccessToken(request)

    return response.getAccessToken()
  }

  async connectJobTaxDataImport(
    dataRequestId: string,
    dataSourceId: string,
    dataSourceName: string,
    jobId: string,
  ) {
    const request = new ConnectJobTaxDataImportRequest()
      .setDataRequestId(dataRequestId)
      .setDataSourceId(dataSourceId)
      .setDataSourceName(dataSourceName)
      .setJobId(jobId)

    await this._api.measureOne.connectJobTaxDataImport(request)
  }

  async getJobTaxDataImport(jobId: string) {
    const request = new GetJobTaxDataImportRequest().setJobId(jobId)

    return this._api.measureOne
      .getJobTaxDataImport(request)
      .then((response) => this.taxJobData.set(mapTaxJobDataImport(response)))
  }

  async startDocumentsUpload(jobId: string, selectedItems: AvailableItem[]) {
    const dataRequestGroupedItems = Object.entries(
      groupBy(selectedItems, 'dataRequestId'),
    ).map(([dataRequestId, items]) =>
      new MeasureOneDataRequestItemSelection()
        .setDataRequestId(dataRequestId)
        .setItemIdsList(items.map((item) => Id.assert(item.id))),
    )

    const request = new StartDocumentsUploadRequest()
      .setJobId(jobId)
      .setDataRequestsList(dataRequestGroupedItems)

    return this._api.measureOne.startDocumentsUpload(request)
  }
}
